import React, { useState, useEffect, useContext } from 'react';
import { Plus, Search, Edit, Trash2, Eye, Settings, Package, AlertCircle, X, RefreshCw, Activity } from 'lucide-react';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import { variablesProductosApi, VariableProducto } from '../../apis/productosDinamicosApi';

// Interface para estadísticas
interface VariablesEstadisticas {
  total_tipos: number;
  tipos_activos: number;
  tipos_inactivos: number;
  total_variables: number;
}

const VariablesProductos: React.FC = () => {
  const authContext = useContext(AuthContext);
  const [variablesProductos, setVariablesProductos] = useState<VariableProducto[]>([]);
  const [estadisticas, setEstadisticas] = useState<VariablesEstadisticas | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [plataformaFilter, setPlataformaFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState<VariableProducto | null>(null);

  // Verificar si el usuario es de Skyangel (plataforma_id = 4)
  const isSkyangel = authContext.usuario?.plataforma_id === 4;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await variablesProductosApi.getAll();
      setVariablesProductos(data);
      calculateEstadisticas(data);
    } catch (error: any) {
      console.error('Error cargando variables de productos:', error);
      if (error.response?.status !== 403) {
        toast.error('Error al cargar los tipos de productos');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateEstadisticas = (data: VariableProducto[]) => {
    const stats: VariablesEstadisticas = {
      total_tipos: data.length,
      tipos_activos: data.filter(v => v.activo).length,
      tipos_inactivos: data.filter(v => !v.activo).length,
      total_variables: data.reduce((sum, v) => sum + v.variables.length, 0)
    };
    setEstadisticas(stats);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este tipo de producto? Esto también eliminará todos los productos asociados.')) {
      try {
        await variablesProductosApi.eliminar(id);
        toast.success('Tipo de producto eliminado exitosamente');
        loadData();
      } catch (error) {
        console.error('Error eliminando tipo de producto:', error);
        toast.error('Error al eliminar el tipo de producto');
      }
    }
  };

  const handleEdit = (variable: VariableProducto) => {
    setSelectedVariable(variable);
    setShowEditModal(true);
  };

  const handleRefresh = async () => {
    await loadData();
  };

  const filteredVariables = variablesProductos.filter(variable => {
    const matchesSearch = variable.producto.toLowerCase().includes(searchTerm.toLowerCase()) ||
      variable.variables.some(v => v.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesPlatform = !plataformaFilter || 
      variable.plataforma?.nombre.toLowerCase().includes(plataformaFilter.toLowerCase());
    
    return matchesSearch && matchesPlatform;
  });

  const styles = `
    .variables-container {
      min-height: 100vh;
      background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
      color: white;
      font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .content-wrapper {
      max-width: 1400px;
      margin: 0 auto;
      padding: 2rem;
    }

    .header-section {
      margin-bottom: 2rem;
    }

    .page-title {
      font-size: 2.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: 0.5rem;
    }

    .page-subtitle {
      color: rgba(255, 255, 255, 0.7);
      font-size: 1.1rem;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1.5rem;
      margin-bottom: 2rem;
    }

    .stat-card {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      padding: 1.5rem;
      transition: all 0.3s ease;
    }

    .stat-card:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(0, 122, 255, 0.3);
      transform: translateY(-2px);
    }

    .stat-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    .stat-title {
      font-size: 0.9rem;
      color: rgba(255, 255, 255, 0.7);
      font-weight: 500;
    }

    .stat-value {
      font-size: 2rem;
      font-weight: 700;
      color: white;
      line-height: 1;
    }

    .controls-section {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
      margin-bottom: 2rem;
      flex-wrap: wrap;
    }

    .search-container {
      position: relative;
      flex: 1;
      max-width: 400px;
    }

    .search-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: rgba(255, 255, 255, 0.5);
    }

    .search-input {
      width: 100%;
      padding: 12px 12px 12px 44px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 12px;
      color: white;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .search-input:focus {
      outline: none;
      background: rgba(255, 255, 255, 0.15);
      border-color: #007AFF;
      box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
    }

    .search-input::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    .action-buttons {
      display: flex;
      gap: 1rem;
      align-items: center;
    }

    .btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      border-radius: 12px;
      font-weight: 500;
      font-size: 14px;
      text-decoration: none;
      transition: all 0.3s ease;
      cursor: pointer;
      border: none;
    }

    .btn-primary {
      background: linear-gradient(135deg, #007AFF 0%, #0056CC 100%);
      color: white;
      box-shadow: 0 4px 15px rgba(0, 122, 255, 0.3);
    }

    .btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 122, 255, 0.4);
    }

    .btn-secondary {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .btn-secondary:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .btn-small {
      padding: 6px 12px;
      font-size: 12px;
    }

    .variables-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
      gap: 1.5rem;
      margin-bottom: 2rem;
    }

    .variable-card {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      padding: 1.5rem;
      transition: all 0.3s ease;
    }

    .variable-card:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(0, 122, 255, 0.3);
      transform: translateY(-2px);
    }

    .card-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    .card-info {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex: 1;
    }

    .card-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.95);
      margin-bottom: 0.25rem;
    }

    .card-subtitle {
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.6);
    }

    .card-actions {
      display: flex;
      gap: 0.5rem;
    }

    .action-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 8px;
      transition: all 0.3s ease;
      cursor: pointer;
      border: none;
    }

    .action-btn.edit {
      background: rgba(245, 158, 11, 0.1);
      color: #F59E0B;
    }

    .action-btn.edit:hover {
      background: rgba(245, 158, 11, 0.2);
    }

    .action-btn.delete {
      background: rgba(239, 68, 68, 0.1);
      color: #EF4444;
    }

    .action-btn.delete:hover {
      background: rgba(239, 68, 68, 0.2);
    }

    .variables-section {
      margin-bottom: 1rem;
    }

    .variables-title {
      font-size: 0.9rem;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.8);
      margin-bottom: 0.5rem;
    }

    .variables-list {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .variable-tag {
      background: rgba(0, 122, 255, 0.1);
      color: #007AFF;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 500;
      border: 1px solid rgba(0, 122, 255, 0.2);
    }

    .card-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
    }

    .status-active {
      background: rgba(16, 185, 129, 0.1);
      color: #10B981;
      border: 1px solid rgba(16, 185, 129, 0.3);
    }

    .status-inactive {
      background: rgba(239, 68, 68, 0.1);
      color: #EF4444;
      border: 1px solid rgba(239, 68, 68, 0.3);
    }

    .card-date {
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.6);
    }

    .loading-spinner {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 4rem;
    }

    .spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(255, 255, 255, 0.1);
      border-top: 3px solid #007AFF;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .empty-state {
      text-align: center;
      padding: 4rem 2rem;
      color: rgba(255, 255, 255, 0.6);
    }

    .empty-icon {
      margin: 0 auto 1rem;
      opacity: 0.5;
    }

    .search-results-indicator {
      animation: slideIn 0.3s ease-out;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (max-width: 768px) {
      .content-wrapper {
        padding: 1rem;
      }

      .page-title {
        font-size: 2rem;
      }

      .controls-section {
        flex-direction: column;
        align-items: stretch;
      }

      .search-container {
        max-width: none;
      }

      .action-buttons {
        justify-content: center;
      }

      .variables-grid {
        grid-template-columns: 1fr;
      }
    }
  `;

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="variables-container">
          <div className="content-wrapper">
            <div className="loading-spinner">
              <div className="spinner" />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="variables-container">
        <div className="content-wrapper">
          {/* Header */}
          <div className="header-section">
            <h1 className="page-title">Tipos de Productos</h1>
            <p className="page-subtitle">
              Administra los tipos de productos y sus variables dinámicas
            </p>
          </div>

          {/* Estadísticas */}
          {estadisticas && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Total Tipos</span>
                  <Settings size={20} color="#007AFF" />
                </div>
                <div className="stat-value">{estadisticas.total_tipos}</div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '0.5rem' }}>
                  {searchTerm ? `${filteredVariables.length} mostrados` : 'Tipos de productos'}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Activos</span>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981' }} />
                </div>
                <div className="stat-value">{estadisticas.tipos_activos}</div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Inactivos</span>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#EF4444' }} />
                </div>
                <div className="stat-value">{estadisticas.tipos_inactivos}</div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Variables</span>
                  <Package size={20} color="#007AFF" />
                </div>
                <div className="stat-value">{estadisticas.total_variables}</div>
              </div>
            </div>
          )}

          {/* Controles */}
          <div className="controls-section">
            <div className="search-container">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                placeholder="Buscar tipos de productos o variables..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            {isSkyangel && (
              <div className="search-container" style={{ marginLeft: '1rem' }}>
                <input
                  type="text"
                  placeholder="Filtrar por plataforma..."
                  value={plataformaFilter}
                  onChange={(e) => setPlataformaFilter(e.target.value)}
                  className="search-input"
                  style={{ paddingLeft: '16px' }}
                />
              </div>
            )}

            <div className="action-buttons">
              <button
                onClick={handleRefresh}
                className="btn btn-secondary"
              >
                <RefreshCw size={16} />
                Actualizar
              </button>

              {authContext.tienePermiso('variables.gestionar') && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn btn-primary"
                >
                  <Plus size={16} />
                  Nuevo Tipo
                </button>
              )}
            </div>
          </div>

          {/* Indicador de resultados */}
          {searchTerm && (
            <div className="search-results-indicator" style={{
              padding: '1rem 2rem',
              background: 'rgba(0, 122, 255, 0.1)',
              border: '1px solid rgba(0, 122, 255, 0.2)',
              borderRadius: '12px',
              marginBottom: '1rem',
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.9rem'
            }}>
              🔍 Mostrando {filteredVariables.length} resultado{filteredVariables.length !== 1 ? 's' : ''} para "{searchTerm}"
            </div>
          )}

          {/* Grid de Variables */}
          <div className="variables-grid">
            {filteredVariables.map((variable) => (
              <div key={variable.id} className="variable-card">
                <div className="card-header">
                  <div className="card-info">
                    <Settings size={24} color="#007AFF" />
                    <div>
                      <div className="card-title">{variable.producto}</div>
                      <div className="card-subtitle">
                        ID: {variable.id} • {variable.variables.length} variables
                        {isSkyangel && variable.plataforma && (
                          <> • <span style={{ color: '#34C759' }}>{variable.plataforma.nombre}</span></>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="card-actions">
                    {authContext.tienePermiso('variables.gestionar') && (
                      <>
                        <button
                          onClick={() => handleEdit(variable)}
                          className="action-btn edit"
                          title="Editar"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(variable.id)}
                          className="action-btn delete"
                          title="Eliminar"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="variables-section">
                  <div className="variables-title">Variables:</div>
                  <div className="variables-list">
                    {variable.variables.map((var_name, index) => (
                      <span key={index} className="variable-tag">
                        {var_name.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="card-footer">
                  <div className={`status-badge ${variable.activo ? 'status-active' : 'status-inactive'}`}>
                    <div style={{ 
                      width: 6, 
                      height: 6, 
                      borderRadius: '50%', 
                      backgroundColor: variable.activo ? '#10B981' : '#EF4444' 
                    }} />
                    {variable.activo ? 'Activo' : 'Inactivo'}
                  </div>
                  <div className="card-date">
                    {new Date(variable.created_at).toLocaleDateString('es-MX')}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Estado vacío */}
          {filteredVariables.length === 0 && !loading && (
            <div className="empty-state">
              <Settings className="empty-icon" size={48} />
              <h3>
                {searchTerm ? 'No se encontraron resultados' : 'No hay tipos de productos'}
              </h3>
              <p>
                {searchTerm 
                  ? 'Intenta con otros términos de búsqueda'
                  : 'Comienza creando tu primer tipo de producto'
                }
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn btn-primary"
                  style={{ marginTop: '1rem' }}
                >
                  <Plus size={16} />
                  Crear Tipo de Producto
                </button>
              )}
            </div>
          )}
        </div>

        {/* Modales */}
        {showCreateModal && (
          <CrearVariableProductoModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              loadData();
            }}
          />
        )}

        {showEditModal && selectedVariable && (
          <EditarVariableProductoModal
            variable={selectedVariable}
            onClose={() => {
              setShowEditModal(false);
              setSelectedVariable(null);
            }}
            onSuccess={() => {
              setShowEditModal(false);
              setSelectedVariable(null);
              loadData();
            }}
          />
        )}
      </div>
    </>
  );
};

// Modal para crear tipo de producto
const CrearVariableProductoModal: React.FC<{
  onClose: () => void;
  onSuccess: () => void;
}> = ({ onClose, onSuccess }) => {
  const [producto, setProducto] = useState('');
  const [variables, setVariables] = useState<string[]>(['']);
  const [loading, setLoading] = useState(false);

  const handleAddVariable = () => {
    setVariables([...variables, '']);
  };

  const handleRemoveVariable = (index: number) => {
    if (variables.length > 1) {
      setVariables(variables.filter((_, i) => i !== index));
    }
  };

  const handleVariableChange = (index: number, value: string) => {
    const newVariables = [...variables];
    newVariables[index] = value;
    setVariables(newVariables);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!producto.trim()) {
      toast.error('El nombre del producto es requerido');
      return;
    }

    const validVariables = variables.filter(v => v.trim() !== '');
    if (validVariables.length === 0) {
      toast.error('Debe agregar al menos una variable');
      return;
    }

    try {
      setLoading(true);
      await variablesProductosApi.crear({
        producto: producto.trim(),
        variables: validVariables.map(v => v.trim().toLowerCase().replace(/\s+/g, '_'))
      });
      toast.success('Tipo de producto creado exitosamente');
      onSuccess();
    } catch (error: any) {
      console.error('Error creando tipo de producto:', error);
      const errorMessage = error.response?.data?.message || 'Error al crear el tipo de producto';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const modalStyles = `
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 1rem;
    }

    .modal-container {
      background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 20px;
      width: 100%;
      max-width: 600px;
      max-height: 90vh;
      overflow-y: auto;
      color: white;
      font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 2rem 2rem 1rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .modal-title {
      font-size: 1.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .close-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 10px;
      color: white;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .close-button:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: scale(1.05);
    }

    .modal-content {
      padding: 2rem;
    }

    .form-group {
      margin-bottom: 1.5rem;
    }

    .form-label {
      display: block;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 0.5rem;
      font-size: 0.9rem;
    }

    .form-label.required::after {
      content: ' *';
      color: #EF4444;
    }

    .form-input {
      width: 100%;
      padding: 12px 16px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: white;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .form-input:focus {
      outline: none;
      background: rgba(255, 255, 255, 0.15);
      border-color: #007AFF;
      box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
    }

    .form-input::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    .variables-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.75rem;
    }

    .add-variable-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #007AFF;
      background: none;
      border: none;
      cursor: pointer;
      font-size: 0.9rem;
      transition: all 0.3s ease;
    }

    .add-variable-btn:hover {
      color: #0056CC;
    }

    .variable-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.75rem;
    }

    .variable-input {
      flex: 1;
    }

    .remove-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      background: rgba(239, 68, 68, 0.1);
      color: #EF4444;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .remove-btn:hover {
      background: rgba(239, 68, 68, 0.2);
    }

    .form-note {
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.6);
      margin-top: 0.5rem;
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      padding: 1.5rem 2rem 2rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 10px;
      font-weight: 500;
      font-size: 14px;
      transition: all 0.3s ease;
      cursor: pointer;
      border: none;
    }

    .btn-secondary {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .btn-secondary:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .btn-primary {
      background: linear-gradient(135deg, #007AFF 0%, #0056CC 100%);
      color: white;
      box-shadow: 0 4px 15px rgba(0, 122, 255, 0.3);
    }

    .btn-primary:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 122, 255, 0.4);
    }

    .btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none !important;
    }

    .spinner {
      width: 16px;
      height: 16px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top: 2px solid white;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  return (
    <>
      <style>{modalStyles}</style>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2 className="modal-title">Crear Tipo de Producto</h2>
            <button onClick={onClose} className="close-button">
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-content">
              {/* Nombre del producto */}
              <div className="form-group">
                <label className="form-label required">
                  Nombre del Producto
                </label>
                <input
                  type="text"
                  value={producto}
                  onChange={(e) => setProducto(e.target.value)}
                  placeholder="Ej: Celular, Televisión, Laptop"
                  className="form-input"
                  required
                />
              </div>

              {/* Variables */}
              <div className="form-group">
                <div className="variables-header">
                  <label className="form-label required">
                    Variables del Producto
                  </label>
                  <button
                    type="button"
                    onClick={handleAddVariable}
                    className="add-variable-btn"
                  >
                    <Plus size={16} />
                    Agregar Variable
                  </button>
                </div>
                
                <div>
                  {variables.map((variable, index) => (
                    <div key={index} className="variable-row">
                      <input
                        type="text"
                        value={variable}
                        onChange={(e) => handleVariableChange(index, e.target.value)}
                        placeholder="Ej: marca, modelo, color, imei"
                        className="form-input variable-input"
                      />
                      {variables.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveVariable(index)}
                          className="remove-btn"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                
                <p className="form-note">
                  Las variables se convertirán automáticamente a formato snake_case (ej: "Número Serie" → "numero_serie")
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? (
                  <>
                    <div className="spinner" />
                    Creando...
                  </>
                ) : (
                  'Crear Tipo'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Modal para editar tipo de producto
const EditarVariableProductoModal: React.FC<{
  variable: VariableProducto;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ variable, onClose, onSuccess }) => {
  const [producto, setProducto] = useState(variable.producto);
  const [variables, setVariables] = useState<string[]>(variable.variables);
  const [loading, setLoading] = useState(false);

  const handleAddVariable = () => {
    setVariables([...variables, '']);
  };

  const handleRemoveVariable = (index: number) => {
    if (variables.length > 1) {
      setVariables(variables.filter((_, i) => i !== index));
    }
  };

  const handleVariableChange = (index: number, value: string) => {
    const newVariables = [...variables];
    newVariables[index] = value;
    setVariables(newVariables);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!producto.trim()) {
      toast.error('El nombre del producto es requerido');
      return;
    }

    const validVariables = variables.filter(v => v.trim() !== '');
    if (validVariables.length === 0) {
      toast.error('Debe agregar al menos una variable');
      return;
    }

    try {
      setLoading(true);
      await variablesProductosApi.actualizar(variable.id, {
        producto: producto.trim(),
        variables: validVariables.map(v => v.trim().toLowerCase().replace(/\s+/g, '_'))
      });
      toast.success('Tipo de producto actualizado exitosamente');
      onSuccess();
    } catch (error: any) {
      console.error('Error actualizando tipo de producto:', error);
      const errorMessage = error.response?.data?.message || 'Error al actualizar el tipo de producto';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const modalStyles = `
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 1rem;
    }

    .modal-container {
      background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 20px;
      width: 100%;
      max-width: 600px;
      max-height: 90vh;
      overflow-y: auto;
      color: white;
      font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 2rem 2rem 1rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .modal-title-section {
      flex: 1;
    }

    .modal-title {
      font-size: 1.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: 0.25rem;
    }

    .modal-subtitle {
      font-size: 0.9rem;
      color: rgba(255, 255, 255, 0.6);
    }

    .close-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 10px;
      color: white;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .close-button:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: scale(1.05);
    }

    .modal-content {
      padding: 2rem;
    }

    .warning-section {
      background: rgba(245, 158, 11, 0.1);
      border: 1px solid rgba(245, 158, 11, 0.3);
      border-radius: 12px;
      padding: 1rem 1.5rem;
      margin-bottom: 1.5rem;
    }

    .warning-header {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
    }

    .warning-content {
      flex: 1;
    }

    .warning-title {
      font-weight: 600;
      color: #F59E0B;
      margin-bottom: 0.25rem;
      font-size: 0.9rem;
    }

    .warning-text {
      color: rgba(245, 158, 11, 0.9);
      font-size: 0.85rem;
      line-height: 1.4;
    }

    .form-group {
      margin-bottom: 1.5rem;
    }

    .form-label {
      display: block;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 0.5rem;
      font-size: 0.9rem;
    }

    .form-label.required::after {
      content: ' *';
      color: #EF4444;
    }

    .form-input {
      width: 100%;
      padding: 12px 16px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: white;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .form-input:focus {
      outline: none;
      background: rgba(255, 255, 255, 0.15);
      border-color: #007AFF;
      box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
    }

    .form-input::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    .variables-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.75rem;
    }

    .add-variable-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #007AFF;
      background: none;
      border: none;
      cursor: pointer;
      font-size: 0.9rem;
      transition: all 0.3s ease;
    }

    .add-variable-btn:hover {
      color: #0056CC;
    }

    .variable-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.75rem;
    }

    .variable-input {
      flex: 1;
    }

    .remove-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      background: rgba(239, 68, 68, 0.1);
      color: #EF4444;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .remove-btn:hover {
      background: rgba(239, 68, 68, 0.2);
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      padding: 1.5rem 2rem 2rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 10px;
      font-weight: 500;
      font-size: 14px;
      transition: all 0.3s ease;
      cursor: pointer;
      border: none;
    }

    .btn-secondary {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .btn-secondary:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .btn-primary {
      background: linear-gradient(135deg, #007AFF 0%, #0056CC 100%);
      color: white;
      box-shadow: 0 4px 15px rgba(0, 122, 255, 0.3);
    }

    .btn-primary:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 122, 255, 0.4);
    }

    .btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none !important;
    }

    .spinner {
      width: 16px;
      height: 16px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top: 2px solid white;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  return (
    <>
      <style>{modalStyles}</style>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title-section">
              <h2 className="modal-title">Editar Tipo de Producto</h2>
              <p className="modal-subtitle">ID: {variable.id}</p>
            </div>
            <button onClick={onClose} className="close-button">
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-content">
              {/* Advertencia */}
              <div className="warning-section">
                <div className="warning-header">
                  <AlertCircle size={20} color="#F59E0B" />
                  <div className="warning-content">
                    <p className="warning-title">¡Atención!</p>
                    <p className="warning-text">
                      Modificar las variables puede afectar los productos existentes de este tipo. 
                      Asegúrate de que los cambios sean compatibles.
                    </p>
                  </div>
                </div>
              </div>

              {/* Nombre del producto */}
              <div className="form-group">
                <label className="form-label required">
                  Nombre del Producto
                </label>
                <input
                  type="text"
                  value={producto}
                  onChange={(e) => setProducto(e.target.value)}
                  placeholder="Ej: Celular, Televisión, Laptop"
                  className="form-input"
                  required
                />
              </div>

              {/* Variables */}
              <div className="form-group">
                <div className="variables-header">
                  <label className="form-label required">
                    Variables del Producto
                  </label>
                  <button
                    type="button"
                    onClick={handleAddVariable}
                    className="add-variable-btn"
                  >
                    <Plus size={16} />
                    Agregar Variable
                  </button>
                </div>
                
                <div>
                  {variables.map((variable_name, index) => (
                    <div key={index} className="variable-row">
                      <input
                        type="text"
                        value={variable_name}
                        onChange={(e) => handleVariableChange(index, e.target.value)}
                        placeholder="Ej: marca, modelo, color, imei"
                        className="form-input variable-input"
                      />
                      {variables.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveVariable(index)}
                          className="remove-btn"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? (
                  <>
                    <div className="spinner" />
                    Actualizando...
                  </>
                ) : (
                  'Actualizar Tipo'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default VariablesProductos;
